import { every } from 'lodash';
import { isSameObject } from 'utils/compare';
import { getChangesBetweenStorySets } from 'utils/story-item-utils';
import { getStoriesForGuide } from 'store/story-items/selectors';
import {
    GUIDE_TAGLINE_MAX_LENGTH,
    GUIDE_CUSTOM_LOCATION_MAX_LENGTH,
} from 'constants/fields-limits';
import { areQuickFactsCorrect } from 'utils/guide-utils';

/**
 * Gets the current guide being edited
 *
 * @param {RootState} state
 * @returns {Guide}
 */
export const getCurrentGuide = (state) => {
    return state.guideEditor.currentGuide;
};

/**
 * Checks if the editor is shown to add a guide (as opposed to editing it)
 *
 * @param {RootState} state
 * @returns {boolean}
 */
export const isAddingGuide = (state) => {
    return state.guideEditor.addingGuide;
};

/**
 * Checks if a new guide is being saved at the moment
 *
 * @param {RootState} state
 * @returns {boolean}
 */
export const isSavingNewGuide = (state) => {
    return state.guideEditor.savingNewGuide;
};

/**
 * Checks if a guide is being patched at the moment
 *
 * @param {RootState} state
 * @returns {boolean}
 */
export const isPatchingGuide = (state) => {
    return state.guideEditor.patchingGuide;
};

/**
 * Checks if a guide is correct enough to be saved (contains all the data it needs)
 *
 * @param {RootState} state
 * @returns {boolean}
 */
export const isGuideCorrect = (state) => {
    const guide = getCurrentGuide(state);

    return (
        !!guide &&
        !!guide.location &&
        !!guide.tagline &&
        guide.tagline.length <= GUIDE_TAGLINE_MAX_LENGTH &&
        guide.location_name_custom.length <= GUIDE_CUSTOM_LOCATION_MAX_LENGTH &&
        areQuickFactsCorrect(guide.quick_facts)
    );
};

/**
 * Gets the original guide being edited (without modifications)
 *
 * @param {RootState} state
 * @returns {Guide}
 */
export const getOriginalGuide = (state) => {
    if (!state.guideEditor.originalGuideId) {
        return null;
    }
    return state.guides.byId[state.guideEditor.originalGuideId] || null;
};

/**
 * Checks if a guide being edited has changed compared to the original (always true if we are adding a new guide)
 *
 * @param {RootState} state
 * @returns {boolean}
 */
export const hasGuideChanged = (state) => {
    if (isAddingGuide(state)) {
        return true;
    }
    const current = getCurrentGuide(state);
    const original = getOriginalGuide(state);

    if (!current || !original) {
        return true;
    }

    return (
        !isSameObject(current.location, original.location) ||
        !isSameObject(current.cover_image, original.cover_image) ||
        !isSameObject(current.guide_type, original.guide_type) ||
        !isSameObject(current.guide_ranking, original.guide_ranking) ||
        !isSameObject(current.main_editor, original.main_editor) ||
        current.main_editor_id !== original.main_editor_id ||
        !isSameObject(current.publish_status, original.publish_status) ||
        current.location_name_custom !== original.location_name_custom ||
        current.tagline !== original.tagline ||
        current.description !== original.description ||
        current.editor_notes !== original.editor_notes ||
        JSON.stringify(current.quick_facts) !==
            JSON.stringify(original.quick_facts) ||
            current.reviewed_at !== original.reviewed_at
    );
};

/**
 * Checks if a guide being edited has changed the location to the original
 *
 * @param {RootState} state
 * @returns {boolean}
 */
export const hasGuideChangedLocation = (state) => {
    if (isAddingGuide(state)) {
        return false;
    }
    const current = getCurrentGuide(state);
    const original = getOriginalGuide(state);

    if (!current || !original) {
        return true;
    }

    return !isSameObject(current.location, original.location);
};

/**
 * Checks if the spot collection editor is being shown for ANY spot collection
 *
 * @param {RootState} state
 * @returns {boolean}
 */
export const isShowingSpotCollectionEditor = (state) => {
    return state.guideEditor.showingSpotCollectionEditor;
};

/**
 * Gets the current stories being edited for a guide
 *
 * @param {RootState} state
 * @returns {StoryItem[]}
 */
export const getCurrentStoriesBeingEdited = (state) => {
    return state.guideEditor.currentStories || [];
};

/**
 * Checks if the stories being edited have changed from the originals
 *
 * @param {RootState} state
 * @returns {boolean}
 */
export const haveStoriesChanged = (state) => {
    const currentGuide = getCurrentGuide(state);
    if (!currentGuide?.id) {
        return false;
    }
    const original = getStoriesForGuide(state, currentGuide.id);
    const current = getCurrentStoriesBeingEdited(state);
    if (original === null || current === null) {
        return false;
    }
    const changes = getChangesBetweenStorySets(current, original);

    return !!(
        changes.deletedStories?.length ||
        changes.editedStories?.length ||
        changes.newStories?.length
    );
};

/**
 * Checks if the stories being edited are filled with needed data
 *
 * @param {RootState} state
 * @returns {boolean}
 */
export const areStoriesComplete = (state) => {
    const currentStories = getCurrentStoriesBeingEdited(state);
    if (!currentStories) {
        return true;
    }
    return every(currentStories, isStoryComplete);
};

/**
 * Checks if a story is complete. The first story does not need a tagline.
 *
 * @param {StoryItem} story
 * @param {number} index
 * @returns {boolean}
 */
function isStoryComplete(story, index) {
    if (index === 0) {
        return !!story.cms_media;
    }
    return !!story.cms_media && !!story.tagline;
}
